/**
 * Profiles, the looking-for-players board, and challenges.
 *
 * The rules, kept pure and in one place because the API enforces them and the
 * dashboard has to predict them — a handle the form accepted and the server
 * rejected is the worst version of this screen.
 *
 * One decision runs through all of it: **there are no private messages.**
 * Everything here publishes something its author wrote, to an audience that all
 * sees the same thing, and contact happens through a link the author chose to
 * publish. A private inbox between strangers is the largest safety obligation a
 * product can take on and the one we are worst placed to meet. See
 * `migrations/038_creator_social.sql` for the long version.
 *
 * The second: nothing is visible until its owner publishes it.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Handles
// ---------------------------------------------------------------------------
/** Length bounds. Short enough to say out loud, long enough to be your own. */
export const HANDLE_MIN = 3;
export const HANDLE_MAX = 24;
/**
 * Names nobody may take.
 *
 * Two kinds, and both are about the same thing: a handle that lets someone pass
 * for a part of the product, or for whoever runs it. "support" asking a
 * streamer for their password is the oldest attack there is, and the cheapest
 * place to stop it is the signup form.
 *
 * Route names are in here too, so a profile can never shadow a page.
 */
export const RESERVED_HANDLES = [
    "admin", "administrator", "root", "support", "help", "helpdesk", "staff",
    "team", "mod", "moderator", "official", "system", "security", "billing",
    "payments", "paddle", "stripe", "streamcut", "stream-cut", "streamcutstudio",
    "api", "www", "mail", "settings", "account", "login", "logout", "signup",
    "register", "password", "profile", "profiles", "clips", "recordings",
    "challenges", "players", "collab", "collabs", "report", "reports", "blocked",
    "me", "you", "null", "undefined", "anonymous", "deleted",
];
/**
 * Whether a handle may be used, and if not, which rule it broke.
 *
 * Returns the problem rather than a boolean so the form can say what to change.
 * "Invalid handle" is a message that makes people guess.
 */
export function handleProblem(raw) {
    const handle = raw.trim();
    if (handle.length < HANDLE_MIN)
        return "too_short";
    if (handle.length > HANDLE_MAX)
        return "too_long";
    // Deliberately narrow: letters, digits, one underscore or hyphen between
    // them. Everything excluded here is excluded because it can be used to build
    // a handle that reads as another one.
    if (!/^[A-Za-z0-9_-]+$/.test(handle))
        return "bad_characters";
    if (!/^[A-Za-z0-9]/.test(handle))
        return "bad_start";
    if (!/[A-Za-z0-9]$/.test(handle))
        return "bad_end";
    if (/[_-]{2,}/.test(handle))
        return "double_separator";
    if (RESERVED_HANDLES.includes(handle.toLowerCase()))
        return "reserved";
    // After stripping separators, a handle must still not be a reserved word:
    // `s-u-p-p-o-r-t` reads as "support" to a person and as something else to a
    // string comparison, and the person is the one being deceived.
    const bare = handle.toLowerCase().replace(/[_-]/g, "");
    if (RESERVED_HANDLES.includes(bare))
        return "confusable";
    return null;
}
export function handleProblemMessage(p) {
    switch (p) {
        case "too_short":
            return `Handles are at least ${HANDLE_MIN} characters.`;
        case "too_long":
            return `Handles are at most ${HANDLE_MAX} characters.`;
        case "bad_characters":
            return "Letters, numbers, hyphens and underscores only.";
        case "bad_start":
            return "Start with a letter or a number.";
        case "bad_end":
            return "End with a letter or a number.";
        case "double_separator":
            return "One hyphen or underscore at a time.";
        case "reserved":
            return "That handle is reserved.";
        case "confusable":
            return "That handle reads as a reserved one. Pick something less like it.";
    }
}
// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------
/**
 * Platforms a profile may link to.
 *
 * A closed list, and every entry is a place a streamer already is. An open
 * "website" field would make this a link farm within a week, and the first
 * thing it would be used for is sending people somewhere we cannot see.
 */
export const LINK_PLATFORMS = ["twitch", "youtube", "kick", "discord", "x", "tiktok"];
export const LINK_LABELS = {
    twitch: "Twitch",
    youtube: "YouTube",
    kick: "Kick",
    discord: "Discord",
    x: "X",
    tiktok: "TikTok",
};
/**
 * Hosts each platform's links must be on.
 *
 * Checked rather than trusted, because the point of a closed list is defeated
 * if `twitch` can hold a link to anywhere. Discord is the exception that proves
 * the rule: an invite is a path on discord.gg, and a username is not a URL at
 * all, so it is stored as text and rendered as text.
 */
const LINK_HOSTS = {
    twitch: ["twitch.tv", "www.twitch.tv"],
    youtube: ["youtube.com", "www.youtube.com", "youtu.be"],
    kick: ["kick.com", "www.kick.com"],
    discord: ["discord.gg", "discord.com", "www.discord.com"],
    x: ["x.com", "www.x.com", "twitter.com", "www.twitter.com"],
    tiktok: ["tiktok.com", "www.tiktok.com"],
};
/**
 * Normalises one link, or rejects it.
 *
 * Returns the canonical `https://` form, so two people who typed the same
 * channel three different ways get one link. A bare handle is accepted for
 * Discord only, where that is what people actually have.
 */
export function normaliseLink(platform, raw) {
    const value = raw.trim();
    if (!value)
        return null;
    if (platform === "discord" && !value.includes("/") && !value.includes("://")) {
        // A Discord username, which is what people actually have. Dots are legal in
        // one, so "contains a dot" cannot be the test for a URL -- the absence of a
        // path is. A bare host from the list below is still treated as a link, so
        // "discord.gg" alone does not become somebody’s username.
        const host = LINK_HOSTS.discord.includes(value.toLowerCase());
        if (!host)
            return /^[A-Za-z0-9._]{2,32}$/.test(value) ? value : null;
    }
    // A scheme is everything up to the first colon, not only the ones followed
    // by "//". Testing for "://" sends `javascript:x` down the prepend branch,
    // where it happens to fail to parse -- refused by luck rather than by the
    // check meant to refuse it. These links are rendered on a public page.
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value)?.[1]?.toLowerCase();
    if (scheme && scheme !== "http" && scheme !== "https")
        return null;
    let url;
    try {
        url = new URL(scheme ? value : `https://${value}`);
    }
    catch {
        return null;
    }
    // http is upgraded rather than rejected: people paste what their browser
    // showed them, and the destination is the same place.
    if (url.protocol !== "https:" && url.protocol !== "http:")
        return null;
    if (!LINK_HOSTS[platform].includes(url.hostname.toLowerCase()))
        return null;
    // Query and fragment dropped: a channel link does not need them, and they are
    // the usual way a tracking parameter rides along.
    return `https://${url.hostname.toLowerCase()}${url.pathname.replace(/\/+$/, "")}`;
}
// ---------------------------------------------------------------------------
// Regions and visibility
// ---------------------------------------------------------------------------
/**
 * Coarse on purpose.
 *
 * Enough to know whether a match is playable without latency ruining it, and
 * not enough to be a fact about where somebody lives.
 */
export const REGIONS = [
    "africa", "asia", "europe", "middle-east", "north-america", "oceania", "south-america",
];
export const REGION_LABELS = {
    africa: "Africa",
    asia: "Asia",
    europe: "Europe",
    "middle-east": "Middle East",
    "north-america": "North America",
    oceania: "Oceania",
    "south-america": "South America",
};
export const LOOKING_FOR = ["none", "duo", "collab", "both"];
/**
 * Whether one person may see another's profile.
 *
 * Four rules, in the order they have to be checked:
 *
 *   1. Your own profile is always yours to see, published or not — otherwise
 *      you could not edit it.
 *   2. A suspended profile is visible to nobody but its owner. This comes
 *      before publication, so republishing cannot undo a suspension.
 *   3. An unpublished profile is visible to nobody but its owner.
 *   4. A block hides each from the other, both ways. The row is one-way; the
 *      effect is not, because the alternative is that blocking someone lets
 *      them keep watching you.
 */
export function canViewProfile(profile, viewerId, blocked) {
    if (viewerId && viewerId === profile.user_id)
        return true;
    if (profile.suspended_at)
        return false;
    if (!profile.published_at)
        return false;
    if (blocked)
        return false;
    return true;
}
// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------
export const LFG_MODES = ["duo", "ranked", "casual", "collab"];
export const LFG_MODE_LABELS = {
    duo: "Looking for a duo",
    ranked: "Ranked",
    casual: "Casual",
    collab: "Streamer collab",
};
/**
 * How long a post lives.
 *
 * Four hours, because the question is "who wants to play now" and a post that
 * outlives the evening wastes the time of everyone who answers it. It also
 * bounds how long anything on the board can sit unnoticed, which matters more
 * than the convenience of a post that lasts all week.
 */
export const LFG_TTL_MS = 4 * 60 * 60 * 1000;
/**
 * Open posts one person may have at a time.
 *
 * Low. Somebody with fifteen posts is not looking for a game, they are using
 * the board as a billboard.
 */
export const LFG_MAX_OPEN = 3;
export const LFG_NOTE_MAX = 280;
/**
 * Whether a post may be made.
 *
 * `profilePublished` is required and is the load-bearing one: a post is only
 * answerable through the links on its author's profile, so an anonymous post is
 * a dead end that wastes a reader's time. It also means everyone on the board
 * has chosen to be there under a name.
 */
export function lfgProblem(input, ctx) {
    if (!ctx.profilePublished)
        return "profile_not_published";
    const name = input.game_name.trim();
    if (!name)
        return "no_game";
    if (name.length > 120)
        return "game_too_long";
    if (!LFG_MODES.includes(input.mode))
        return "bad_mode";
    if ((input.note ?? "").length > LFG_NOTE_MAX)
        return "note_too_long";
    const size = input.party_size ?? 2;
    if (!Number.isInteger(size) || size < 2 || size > 100)
        return "bad_party_size";
    if (ctx.openPosts >= LFG_MAX_OPEN)
        return "too_many_open";
    return null;
}
export function lfgProblemMessage(p) {
    switch (p) {
        case "profile_not_published":
            return ("Publish your profile first. A post is answered through the links on " +
                "your profile, so an anonymous one has no way for anybody to reach you.");
        case "no_game":
            return "Say which game.";
        case "game_too_long":
            return "That game name is too long.";
        case "bad_mode":
            return "Pick what kind of game you want.";
        case "note_too_long":
            return `Keep the note under ${LFG_NOTE_MAX} characters.`;
        case "bad_party_size":
            return "Party size is between 2 and 100.";
        case "too_many_open":
            return `You already have ${LFG_MAX_OPEN} posts up. Close one first.`;
    }
}
/** When a post made now should expire. */
export function lfgExpiry(now) {
    return new Date(now + LFG_TTL_MS);
}
/** Whether a post should still be shown. */
export function isOpen(post, now) {
    if (post.closed_at || post.hidden_at)
        return false;
    return new Date(post.expires_at).getTime() > now;
}
/** How much life a post has left, for the board. */
export function expiresIn(post, now) {
    const ms = new Date(post.expires_at).getTime() - now;
    if (ms <= 0)
        return "expired";
    const mins = Math.round(ms / 60_000);
    if (mins < 60)
        return `${mins} min left`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m left`;
}
// ---------------------------------------------------------------------------
// Challenges
// ---------------------------------------------------------------------------
export const CHALLENGE_METRICS = [
    "clips_rendered",
    "sessions",
    "new_game",
    "audience_score",
];
export function challengeProgress(c, facts) {
    switch (c.metric) {
        case "clips_rendered":
            return facts.clipsRendered;
        case "sessions":
            return facts.sessions;
        case "new_game":
            return facts.newGames;
        case "audience_score":
            return facts.bestAudienceScore;
    }
}
/**
 * The challenge as the dashboard shows it.
 *
 * `completedAt` wins over a recomputed progress that has since fallen below the
 * target. Deleting a clip next month must not take a badge back — the badge is
 * a fact about a week that happened.
 */
export function challengeState(c, facts, entry, now) {
    const progress = entry.joined ? challengeProgress(c, facts) : 0;
    const done = entry.completedAt !== null || progress >= c.target;
    const active = new Date(c.starts_at).getTime() <= now && new Date(c.ends_at).getTime() > now;
    return {
        challenge: c,
        joined: entry.joined,
        completedAt: entry.completedAt,
        progress,
        share: done ? 1 : Math.min(1, c.target > 0 ? progress / c.target : 0),
        active,
        label: !entry.joined
            ? "Not joined"
            : done
                ? "Done"
                : active
                    ? `${progress} of ${c.target}`
                    : `Ended at ${progress} of ${c.target}`,
    };
}
// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------
/**
 * Why something is being reported.
 *
 * A short closed list. A free-text-only report form produces a queue nobody can
 * triage, and the categories here are the ones that need different responses.
 */
export const REPORT_REASONS = [
    "impersonation",
    "harassment",
    "spam",
    "sexual_content",
    "scam",
    "underage",
    "other",
];
export const REPORT_REASON_LABELS = {
    impersonation: "Pretending to be someone else",
    harassment: "Harassment or hate",
    spam: "Spam or advertising",
    sexual_content: "Sexual content",
    scam: "Scam or phishing",
    underage: "Appears to be a child",
    other: "Something else",
};
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
export const ProfileInput = z.object({
    handle: z.string().min(1).max(64).optional(),
    display_name: z.string().min(1).max(50).nullable().optional(),
    bio: z.string().max(500).nullable().optional(),
    region: z.enum(REGIONS).nullable().optional(),
    languages: z.array(z.string().min(2).max(32)).max(5).optional(),
    looking_for: z.enum(LOOKING_FOR).optional(),
    links: z.record(z.string(), z.string().max(300)).optional(),
    /** Explicit, and the only way anything here becomes public. */
    published: z.boolean().optional(),
});
export const LfgInput = z.object({
    game_name: z.string().min(1).max(120),
    game_id: z.string().max(120).nullable().optional(),
    mode: z.enum(LFG_MODES),
    note: z.string().max(LFG_NOTE_MAX).nullable().optional(),
    region: z.enum(REGIONS).nullable().optional(),
    language: z.string().min(2).max(32).nullable().optional(),
    party_size: z.number().int().min(2).max(100).optional(),
});
export const ReportInput = z.object({
    subject_handle: z.string().max(64).optional(),
    subject_post: z.string().max(64).optional(),
    reason: z.enum(REPORT_REASONS),
    detail: z.string().max(1000).nullable().optional(),
});
//# sourceMappingURL=social.js.map